import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Database, Globe, FileText, Cloud, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { ConnectionForm } from './ConnectionForm';
import { ConnectionTester } from './ConnectionTester';
import { useToast } from '@/hooks/use-toast';

export interface DataSourceType {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  category: 'database' | 'api' | 'file' | 'cloud';
  configFields: Array<{
    name: string;
    label: string;
    type: 'text' | 'password' | 'number' | 'url' | 'file';
    required: boolean;
    placeholder?: string;
    helpText?: string;
  }>;
}

export interface DataSource {
  id: string;
  name: string;
  type: string;
  config: Record<string, any>;
  status: 'connected' | 'disconnected' | 'testing' | 'error';
  lastTested?: Date;
  errorMessage?: string;
}

export interface DataSourceConnectorProps {
  onConnectionSuccess: (source: DataSource) => void;
  onConnectionError: (error: Error) => void;
  existingSources?: DataSource[];
  supportedTypes?: DataSourceType[];
}

const defaultSupportedTypes: DataSourceType[] = [
  {
    id: 'mongodb',
    name: 'MongoDB',
    icon: <Database className="h-6 w-6" />,
    description: 'Connect to MongoDB database for document-based analytics',
    category: 'database',
    configFields: [
      { name: 'connectionString', label: 'Connection String', type: 'text', required: true, placeholder: 'mongodb://localhost:27017/database' },
      { name: 'database', label: 'Database Name', type: 'text', required: true, placeholder: 'analytics_db' },
      { name: 'username', label: 'Username', type: 'text', required: false },
      { name: 'password', label: 'Password', type: 'password', required: false },
    ]
  },
  {
    id: 'postgresql',
    name: 'PostgreSQL',
    icon: <Database className="h-6 w-6" />,
    description: 'Connect to PostgreSQL database for relational analytics',
    category: 'database',
    configFields: [
      { name: 'host', label: 'Host', type: 'text', required: true, placeholder: 'localhost' },
      { name: 'port', label: 'Port', type: 'number', required: true, placeholder: '5432' },
      { name: 'database', label: 'Database', type: 'text', required: true, placeholder: 'analytics' },
      { name: 'username', label: 'Username', type: 'text', required: true },
      { name: 'password', label: 'Password', type: 'password', required: true },
    ]
  },
  {
    id: 'rest-api',
    name: 'REST API',
    icon: <Globe className="h-6 w-6" />,
    description: 'Connect to RESTful web services and APIs',
    category: 'api',
    configFields: [
      { name: 'baseUrl', label: 'Base URL', type: 'url', required: true, placeholder: 'https://api.example.com' },
      { name: 'apiKey', label: 'API Key', type: 'password', required: false },
      { name: 'authHeader', label: 'Auth Header', type: 'text', required: false, placeholder: 'Authorization' },
      { name: 'timeout', label: 'Timeout (ms)', type: 'number', required: false, placeholder: '30000' },
    ]
  },
  {
    id: 'csv',
    name: 'CSV Files',
    icon: <FileText className="h-6 w-6" />,
    description: 'Upload and analyze CSV data files',
    category: 'file',
    configFields: [
      { name: 'file', label: 'CSV File', type: 'file', required: true },
      { name: 'delimiter', label: 'Delimiter', type: 'text', required: false, placeholder: ',' },
      { name: 'hasHeaders', label: 'Has Headers', type: 'text', required: false, placeholder: 'true' },
    ]
  },
  {
    id: 'weather-api',
    name: 'Weather API',
    icon: <Cloud className="h-6 w-6" />,
    description: 'Connect to weather services for agricultural analytics',
    category: 'api',
    configFields: [
      { name: 'provider', label: 'Provider', type: 'text', required: true, placeholder: 'openweathermap' },
      { name: 'apiKey', label: 'API Key', type: 'password', required: true },
      { name: 'region', label: 'Default Region', type: 'text', required: false, placeholder: 'US' },
    ]
  }
];

export const DataSourceConnector: React.FC<DataSourceConnectorProps> = ({
  onConnectionSuccess,
  onConnectionError,
  existingSources = [],
  supportedTypes = defaultSupportedTypes
}) => {
  const [selectedType, setSelectedType] = useState<DataSourceType | null>(null);
  const [activeTab, setActiveTab] = useState<string>('new');
  const [isConnecting, setIsConnecting] = useState(false);
  const { toast } = useToast();

  const handleConnectionTest = useCallback(async (config: Record<string, any>) => {
    if (!selectedType) return;

    setIsConnecting(true);
    try {
      // Create a test data source
      const testSource: DataSource = {
        id: `test-${Date.now()}`,
        name: config.name || `${selectedType.name} Connection`,
        type: selectedType.id,
        config,
        status: 'testing'
      };

      // This would call the backend service to test the connection
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call

      const connectedSource: DataSource = {
        ...testSource,
        status: 'connected',
        lastTested: new Date()
      };

      onConnectionSuccess(connectedSource);

      toast({
        title: "Connection Successful",
        description: `Successfully connected to ${selectedType.name}`,
      });

      setSelectedType(null);
      setActiveTab('existing');
    } catch (error) {
      const connectionError = error instanceof Error ? error : new Error('Connection failed');
      onConnectionError(connectionError);

      toast({
        title: "Connection Failed",
        description: connectionError.message,
        variant: "destructive"
      });
    } finally {
      setIsConnecting(false);
    }
  }, [selectedType, onConnectionSuccess, onConnectionError, toast]);

  const getStatusIcon = (status: DataSource['status']) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'testing':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadgeVariant = (status: DataSource['status']) => {
    switch (status) {
      case 'connected':
        return 'default';
      case 'testing':
        return 'secondary';
      case 'error':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const groupedTypes = supportedTypes.reduce((acc, type) => {
    if (!acc[type.category]) acc[type.category] = [];
    acc[type.category].push(type);
    return acc;
  }, {} as Record<string, DataSourceType[]>);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Data Source Connector
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="new">Connect New</TabsTrigger>
            <TabsTrigger value="existing">Existing Sources ({existingSources.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="new" className="space-y-4">
            {!selectedType ? (
              <div className="space-y-6">
                {Object.entries(groupedTypes).map(([category, types]) => (
                  <div key={category} className="space-y-3">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                      {category} Sources
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {types.map((type) => (
                        <Card
                          key={type.id}
                          className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-blue-200 dark:hover:border-blue-800"
                          onClick={() => setSelectedType(type)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="text-blue-600 dark:text-blue-400">
                                {type.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm">{type.name}</h4>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  {type.description}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-blue-600 dark:text-blue-400">
                      {selectedType.icon}
                    </div>
                    <div>
                      <h3 className="font-medium">{selectedType.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedType.description}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedType(null)}
                  >
                    Back
                  </Button>
                </div>

                <ConnectionForm
                  dataSourceType={selectedType}
                  onSubmit={handleConnectionTest}
                  isSubmitting={isConnecting}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="existing" className="space-y-4">
            {existingSources.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No data sources connected yet</p>
                <p className="text-sm">Connect your first data source to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {existingSources.map((source) => (
                  <Card key={source.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(source.status)}
                          <div>
                            <h4 className="font-medium">{source.name}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {supportedTypes.find(t => t.id === source.type)?.name || source.type}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getStatusBadgeVariant(source.status)}>
                            {source.status}
                          </Badge>
                          <ConnectionTester
                            dataSource={source}
                            onTestResult={(result) => {
                              if (result.success) {
                                toast({
                                  title: "Connection Test Passed",
                                  description: `${source.name} is working correctly`,
                                });
                              } else {
                                toast({
                                  title: "Connection Test Failed",
                                  description: result.error || "Unknown error occurred",
                                  variant: "destructive"
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                      {source.lastTested && (
                        <p className="text-xs text-gray-500 mt-2">
                          Last tested: {source.lastTested.toLocaleString()}
                        </p>
                      )}
                      {source.errorMessage && (
                        <p className="text-xs text-red-600 mt-1">
                          Error: {source.errorMessage}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};